import type { RequestContext } from "../context/request-context.js";
import type { EquipmentService } from "../../equipment/equipment-service.js";
import type { ReportService } from "./report-service.js";
import type { MinutaService } from "./minuta-service.js";
import type { AvrReportService } from "./avr-report-service.js";
import type { InspectionBookService } from "./inspection-book-service.js";
import type { ProjectService } from "./project-service.js";
import type { TechnicalDocumentService } from "./technical-document-service.js";
import type { DocumentVisual } from "../../domain/studio/visual-design.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { documentEngine, type DocumentSnapshot } from "../../infrastructure/documents/document-engine.js";
import { currentRevisionOf, type TechnicalDocumentType } from "../../domain/documentation/technical-document.js";
import {
  resolveInspectionBookContext,
  applicableSectionsFor,
  BOOK_SECTION_LABELS,
  type ResolvedBookContext,
  type BookSectionId
} from "../../domain/inspection-book/book-context-resolver.js";
import type { GeneratedLineList } from "../../domain/inspection-book/line-list.js";
import { PipingSystemLineListService } from "./piping-system-line-list-service.js";

/**
 * BOOK DE INSPEÇÃO — CONSOLIDADOR DOCUMENTAL (comando: remodelação da aba
 * Book de Inspeção). Este serviço NUNCA cria, edita ou anexa documento —
 * só localiza, filtra pelo vínculo real e organiza o que já existe nos
 * módulos oficiais (RI, END, AVR, Minuta, anexos de Equipamento, PIT já
 * gravado pelo antigo Prontuário/NR-13). Não confundir com InspectionBookService (o dossiê
 * NR-13 por Inspeção, com validação/emissão/assinatura) — este é outro
 * conceito, só leitura, e não altera aquele em nada.
 */

const NDT_STATUS_LABELS: Record<string, string> = { DRAFT: "Em Elaboração", VALIDATED: "Validado", SIGNED: "Assinado" };
const INTEGRITY_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Rascunho", IN_ELABORATION: "Em Elaboração", FINALIZED_BY_INSPECTOR: "Finalizado pelo Inspetor",
  SUBMITTED_TO_ENGINEERING: "Aguardando Análise do PLH", UNDER_ENGINEERING_REVIEW: "Em Análise do PLH",
  RETURNED: "Correção Solicitada", IN_CORRECTION: "Correção em Andamento", APPROVED: "Aprovado pelo PLH",
  AWAITING_SIGNATURE: "Aguardando Assinatura", SIGNED: "Assinado", COMPLETED: "Finalizado"
};
const BOOK_ITEM_STATUS_LABELS: Record<string, string> = {
  NOT_EVALUATED: "Não Avaliado", REQUIRED: "Obrigatório", PENDING: "Pendente", IN_PROGRESS: "Em Andamento",
  AVAILABLE: "Disponível", UNDER_REVIEW: "Em Análise", APPROVED: "Aprovado", SIGNED: "Assinado",
  ISSUED: "Emitido", NOT_APPLICABLE: "Não Aplicável", BLOCKED: "Bloqueado", REJECTED: "Rejeitado", SUPERSEDED: "Substituído"
};

export type BookDocumentOrigin = "RI" | "ME" | "LP" | "PM" | "USC" | "BSCAN" | "AVR" | "MINUTA" | "EQUIPAMENTO" | "PRONTUARIO_NR13" | "BIBLIOTECA_TECNICA" | "INSPECT_IA";

/** Uma linha de documento — a mesma forma para qualquer origem, é só o que a tela precisa mostrar/baixar. */
export interface BookDocumentRow {
  id: string;
  origin: BookDocumentOrigin;
  originLabel: string;
  number: string;
  revision: string | null;
  date: string;
  status: string | null;
  statusLabel: string | null;
  responsible: string | null;
  /** Rota da própria página do documento (VISUALIZAR) — nunca um caminho de storage. */
  viewRoute: string | null;
  /** Rota/handler de download pelo backend autorizado — nunca /data/files direto (comando item 32). */
  downloadRoute: string | null;
  isDraft: boolean;
}

export interface BookSection {
  id: BookSectionId;
  label: string;
  applicable: boolean;
  /** false quando o usuário não tem a permissão de leitura da origem — a seção some, não erra (RBAC, item 31). */
  authorized: boolean;
  rows: BookDocumentRow[];
}

export interface InspectionBookConsolidated {
  context: ResolvedBookContext;
  sections: BookSection[];
  /**
   * LISTA DE LINHAS gerada automaticamente a partir do cadastro oficial —
   * presente somente quando o contexto é SISTEMA DE TUBULAÇÃO. `null` em
   * qualquer outro tipo de item (Linha individual, vaso, tanque, caldeira…).
   * Quando o Sistema existe mas não tem Linhas, vem com `totalLines: 0` para
   * a tela mostrar a mensagem própria — nunca "não encontrado".
   */
  generatedLineList: GeneratedLineList | null;
}

export interface EquipmentOption {
  id: string;
  tag: string;
  name: string;
  contextType: string;
  contextTypeLabel: string;
}

interface Deps {
  equipmentService: EquipmentService;
  reportService: ReportService;
  minutaService: MinutaService;
  avrReportService: AvrReportService;
  inspectionBookService: InspectionBookService;
  projectService: ProjectService;
  /** Documentação e Biblioteca Técnica (Bloco 03) — opcional para não quebrar composições antigas do consolidador. */
  technicalDocumentService?: TechnicalDocumentService;
  ndt: {
    LP: { listByClient(context: RequestContext, clientId: string): Promise<readonly any[]> };
    ME: { listByClient(context: RequestContext, clientId: string): Promise<readonly any[]> };
    PM: { listByClient(context: RequestContext, clientId: string): Promise<readonly any[]> };
    USC: { listByClient(context: RequestContext, clientId: string): Promise<readonly any[]> };
    /** Opcional: composições antigas do Book continuam válidas sem ele. */
    BSCAN?: { listByClient(context: RequestContext, clientId: string): Promise<readonly any[]> };
  };
}

const isoOnly = (value: string | null | undefined): string => (value ? String(value).slice(0, 10) : "");

export class InspectionBookConsolidatorService {
  private readonly lineLists: PipingSystemLineListService;

  constructor(private readonly deps: Deps) {
    this.lineLists = new PipingSystemLineListService(this.deps.equipmentService);
  }

  /**
   * STUDIO INSPECT IA — o mesmo padrão do RI: sem provedor ligado, o builder
   * recebe `visual: null` e imprime exatamente como sempre (nenhuma mudança).
   * Ligado (via `wireStudioDesign`), lê a versão carimbada na emissão do
   * Book, ou a vigente publicada enquanto ele ainda é preliminar.
   */
  private documentVisual: ((bookId: string, closed: boolean) => Promise<DocumentVisual>) | null = null;

  attachDocumentVisual(provider: (bookId: string, closed: boolean) => Promise<DocumentVisual>): void {
    this.documentVisual = provider;
  }

  private async resolveVisual(bookId: string, closed: boolean): Promise<DocumentVisual | null> {
    return this.documentVisual ? await this.documentVisual(bookId, closed).catch(() => null) : null;
  }

  /** Equipamentos do Cliente/Projeto/Unidade selecionados — a lista do seletor "ITEM" (item 1/20). Nunca por nome de arquivo, sempre pelo cadastro real. */
  async listItems(context: RequestContext, filter: { projectId?: string | null; unitAreaId?: string | null }): Promise<EquipmentOption[]> {
    const equipments = await this.deps.equipmentService.listEquipments(context, { status: "ACTIVE" });
    return equipments
      .filter(eq => (!filter.projectId || String(eq.projectId || "") === String(filter.projectId)))
      .filter(eq => (!filter.unitAreaId || String(eq.unitAreaId || "") === String(filter.unitAreaId)))
      .map(eq => {
        const ctx = resolveInspectionBookContext(eq);
        return { id: String(eq.id), tag: eq.tag || "", name: eq.name || "", contextType: ctx.contextType, contextTypeLabel: ctx.contextTypeLabel };
      })
      .sort((a, b) => (a.tag || a.name).localeCompare(b.tag || b.name, "pt-BR"));
  }

  /**
   * Monta o Book consolidado do Equipamento selecionado: resolve o contexto
   * uma única vez (item 14) e cada seção filtra estritamente por esse mesmo
   * contexto — nunca por nome de arquivo, nunca misturando outro Projeto,
   * Unidade, Sistema ou Equipamento (itens 2 e 13).
   */
  async loadBook(context: RequestContext, equipmentId: string): Promise<InspectionBookConsolidated> {
    const equipment = await this.deps.equipmentService.getEquipment(context, equipmentId);
    if (!equipment) throw new Error("Equipamento não encontrado.");
    const bookContext = resolveInspectionBookContext(equipment);
    const applicable = new Set(applicableSectionsFor(bookContext.contextType));

    const inspections = await this.inspectionsOf(context, equipmentId);
    const inspectionIds = inspections.map(i => String(i.id));

    // LISTA DE LINHAS automática: só para SISTEMA DE TUBULAÇÃO, sempre pela
    // fonte única (PipingSystemLineListService → cadastro oficial). Independe
    // de existir a Lista original anexada — arquivo ausente ≠ Lista automática
    // ausente. A mesma composição alimenta o RI e o PDF independente.
    const generatedLineList = this.can(context, PermissionCode.EquipmentRead)
      ? await this.lineLists.buildIfSystem(context, equipment).catch(() => null)
      : null;

    const sections: BookSection[] = [];
    sections.push(await this.reportsSection(context, bookContext, inspectionIds, applicable.has("RELATORIOS")));
    sections.push(await this.minutasSection(context, bookContext, applicable.has("MINUTAS")));
    sections.push(await this.equipmentDocumentSection(context, bookContext, "ISOMETRICOS", ["ISOMETRIC"], applicable.has("ISOMETRICOS"), PermissionCode.EquipmentRead, "ISOMETRIC"));
    sections.push(await this.lineListSection(context, bookContext, applicable.has("LISTA_DE_LINHAS"), generatedLineList));
    sections.push(await this.equipmentDocumentSection(context, bookContext, "FOLHA_DE_DADOS", ["DATASHEET"], applicable.has("FOLHA_DE_DADOS"), PermissionCode.EquipmentRead, "DATASHEET"));
    sections.push(await this.equipmentDocumentSection(context, bookContext, "FLUXOGRAMAS", ["PID", "PFD", "FLOWCHART", "FLUXOGRAMA"], applicable.has("FLUXOGRAMAS"), PermissionCode.EquipmentRead, "FLOWCHART"));
    sections.push(await this.pitSection(context, bookContext, inspectionIds, applicable.has("PITS")));

    return { context: bookContext, sections, generatedLineList };
  }

  /**
   * LISTA DE LINHAS: a Lista gerada pelo INSPECT IA e a Lista original
   * anexada (Biblioteca/importação) COEXISTEM. A original nunca é
   * sobrescrita; a automática nunca depende dela.
   */
  private async lineListSection(context: RequestContext, bookContext: ResolvedBookContext, applicable: boolean, generated: GeneratedLineList | null): Promise<BookSection> {
    const section = await this.equipmentDocumentSection(context, bookContext, "LISTA_DE_LINHAS", ["LINE_LIST"], applicable, PermissionCode.EquipmentRead, "LINE_LIST");
    // Sistema sem Linhas não vira linha de documento: a tela mostra a mensagem
    // própria ("SISTEMA SEM LINHAS CADASTRADAS", total 0), nunca um item vazio.
    if (!applicable || !section.authorized || !generated || generated.totalLines === 0) return section;
    section.rows.unshift({
      id: `LINE_LIST:${generated.systemId}`,
      origin: "INSPECT_IA",
      originLabel: "Gerada automaticamente",
      number: "LISTA DE LINHAS",
      revision: null,
      date: isoOnly(generated.generatedAt),
      status: "GENERATED",
      statusLabel: `${generated.totalLines} ${generated.totalLines === 1 ? "linha" : "linhas"}`,
      responsible: null,
      viewRoute: null,
      downloadRoute: null,
      isDraft: false
    });
    return section;
  }

  private async inspectionsOf(context: RequestContext, equipmentId: string): Promise<readonly { id: string }[]> {
    const inspectionRepo = this.deps.reportService.inspectionRepo;
    if (!inspectionRepo?.findByEquipment) return [];
    const list = await inspectionRepo.findByEquipment(context, equipmentId as any).catch(() => []);
    return (list || []).map(i => ({ id: String(i.id) }));
  }

  private can(context: RequestContext, permission: string): boolean {
    return !!(context.permissions && typeof (context.permissions as any).has === "function" && (context.permissions as any).has(permission));
  }

  // -------------------------------------------------------------- RELATÓRIOS

  private async reportsSection(context: RequestContext, bookContext: ResolvedBookContext, inspectionIds: string[], applicable: boolean): Promise<BookSection> {
    const rows: BookDocumentRow[] = [];
    if (applicable) {
      if (this.can(context, PermissionCode.RiRead) || this.can(context, PermissionCode.IntegrityReportView)) {
        for (const inspectionId of inspectionIds) {
          const report = await this.deps.reportService.reportRepo.findByInspection(context, inspectionId as any).catch(() => null);
          if (!report) continue;
          rows.push({
            id: `RI:${report.id}`, origin: "RI", originLabel: "Relatório de Integridade",
            number: report.reportNumber || String(report.id), revision: report.revision ?? null,
            date: isoOnly((report as any).updatedAt || (report as any).createdAt),
            status: report.status, statusLabel: INTEGRITY_STATUS_LABELS[report.status] || report.status,
            responsible: null, viewRoute: `/reports/integrity/view/${report.id}`, downloadRoute: `/reports/integrity/view/${report.id}`,
            isDraft: report.status === "DRAFT" || report.status === "IN_ELABORATION"
          });
        }
      }
      const ndtSources: Array<{ type: "ME" | "LP" | "PM" | "USC" | "BSCAN"; label: string; permission: string; route: string }> = [
        { type: "ME", label: "Relatório ME", permission: PermissionCode.MeReportView, route: "/ndt/thickness" },
        { type: "LP", label: "Relatório LP", permission: PermissionCode.LpReportView, route: "/ndt/lp" },
        { type: "PM", label: "Relatório PM", permission: PermissionCode.PmReportView, route: "/ndt/pm" },
        { type: "USC", label: "Relatório US-Convencional", permission: PermissionCode.UscReportView, route: "/ndt/us-conventional" },
        { type: "BSCAN", label: "Relatório B-Scan / C-Scan", permission: PermissionCode.BscanReportView, route: "/ndt/bscan" }
      ];
      for (const src of ndtSources) {
        if (!this.can(context, src.permission)) continue;
        const reader = this.deps.ndt[src.type];
        if (!reader) continue;
        const all = await reader.listByClient(context, bookContext.clientId).catch(() => []);
        for (const r of all as any[]) {
          if (String(r.equipmentId || "") !== bookContext.equipmentId) continue;
          rows.push({
            id: `${src.type}:${r.id}`, origin: src.type, originLabel: src.label,
            number: r.reportNumber || String(r.id), revision: r.revision ?? null, date: isoOnly(r.testDate || r.updatedAt),
            status: r.status, statusLabel: NDT_STATUS_LABELS[r.status] || r.status, responsible: r.inspectorName || null,
            viewRoute: `${src.route}/${r.id}`, downloadRoute: `${src.route}/${r.id}`, isDraft: r.status === "DRAFT"
          });
        }
      }
      if (this.can(context, PermissionCode.AvrRead)) {
        const avrs = await this.deps.avrReportService.listByEquipment(context, bookContext.equipmentId).catch(() => []);
        for (const a of avrs as any[]) {
          rows.push({
            id: `AVR:${a.id}`, origin: "AVR", originLabel: "AVR", number: a.avrNumber || String(a.id),
            revision: a.revision ?? null, date: isoOnly(a.updatedAt || a.calculatedAt),
            status: a.status, statusLabel: a.status, responsible: null,
            viewRoute: `/integrity/avr/${a.id}`, downloadRoute: `/integrity/avr/${a.id}`, isDraft: a.status === "DRAFT"
          });
        }
      }
    }
    rows.sort((a, b) => b.date.localeCompare(a.date) || a.number.localeCompare(b.number, "pt-BR"));
    return this.buildSection("RELATORIOS", applicable, rows, true);
  }

  // ----------------------------------------------------------------- MINUTAS

  private async minutasSection(context: RequestContext, bookContext: ResolvedBookContext, applicable: boolean): Promise<BookSection> {
    const authorized = this.can(context, PermissionCode.RiRead) || this.can(context, PermissionCode.IntegrityReportView);
    let rows: BookDocumentRow[] = [];
    if (applicable && authorized) {
      const minutas = await this.deps.minutaService.listByEquipment(context, bookContext.equipmentId);
      rows = minutas.map(m => ({
        id: `MINUTA:${m.id}`, origin: "MINUTA" as const, originLabel: "Minuta Técnica",
        number: m.trackingNumber, revision: m.currentRevision, date: isoOnly((m as any).updatedAt),
        status: m.status, statusLabel: m.status, responsible: null,
        viewRoute: `/integrity/recommendations?minuta=${m.id}`, downloadRoute: `/integrity/recommendations?minuta=${m.id}`,
        isDraft: m.status === "AGUARDANDO ANÁLISE" || m.status === "EM ANÁLISE"
      })).sort((a, b) => b.date.localeCompare(a.date));
    }
    return this.buildSection("MINUTAS", applicable, rows, authorized);
  }

  // --------------------------------------------------- ANEXOS DE EQUIPAMENTO
  // (Isométricos, Lista de Linhas, Folha de Dados, Fluxogramas — sempre pelo
  // vínculo real EquipmentDocument.equipmentId, nunca pelo nome do arquivo.)

  private async equipmentDocumentSection(context: RequestContext, bookContext: ResolvedBookContext, section: BookSectionId, documentTypes: readonly string[], applicable: boolean, permission: string, technicalDocumentType?: TechnicalDocumentType): Promise<BookSection> {
    const authorized = this.can(context, permission);
    let rows: BookDocumentRow[] = [];
    if (applicable && authorized) {
      const documents = await this.deps.equipmentService.listDocuments(context, bookContext.equipmentId).catch(() => []);
      const wanted = new Set(documentTypes.map(t => t.toUpperCase()));
      rows = documents
        .filter(doc => wanted.has(String(doc.documentType || "").toUpperCase()))
        .map(doc => ({
          id: `DOC:${doc.id}`, origin: "EQUIPAMENTO" as const, originLabel: "Anexo do Equipamento",
          number: doc.title || doc.filename, revision: null, date: isoOnly(doc.uploadedAt),
          status: null, statusLabel: null, responsible: doc.uploadedBy || null,
          viewRoute: doc.fileUrl || null, downloadRoute: doc.fileUrl || null, isDraft: false
        }));
      // Documentação e Biblioteca Técnica (Bloco 03): mesmo Equipamento, mesmo
      // tipo de documento — mescla para não duplicar catálogo nem esconder o
      // que já foi anexado pela tela nova.
      if (technicalDocumentType) {
        rows.push(...(await this.technicalDocumentRows(context, bookContext, technicalDocumentType)));
      }
      rows.sort((a, b) => b.date.localeCompare(a.date));
    }
    return this.buildSection(section, applicable, rows, authorized);
  }

  /** Linhas oriundas do TechnicalDocumentService (Bloco 03) — sempre a revisão vigente. */
  private async technicalDocumentRows(context: RequestContext, bookContext: ResolvedBookContext, documentType: TechnicalDocumentType): Promise<BookDocumentRow[]> {
    if (!this.deps.technicalDocumentService || !this.can(context, PermissionCode.DocumentRead)) return [];
    const entries = await this.deps.technicalDocumentService.search(context, { equipmentId: bookContext.equipmentId, documentType, status: "ACTIVE" }).catch(() => []);
    return entries.map(entry => {
      const rev = currentRevisionOf(entry);
      return {
        id: `DOCTEC:${entry.document.id}`, origin: "BIBLIOTECA_TECNICA" as const, originLabel: "Biblioteca Técnica",
        number: entry.document.title, revision: rev?.revision ?? null, date: isoOnly(rev?.documentDate || rev?.uploadedAt),
        status: entry.document.status, statusLabel: entry.document.status === "ACTIVE" ? "Ativo" : "Inativo",
        responsible: rev?.uploadedBy || null,
        viewRoute: "/documentation/library", downloadRoute: "/documentation/library", isDraft: false
      };
    });
  }

  // -------------------------------------------------------------------- PIT
  // (PIT elaborado no antigo Prontuário Técnico/NR-13 — tela DESCONTINUADA em 2026-09-26.
  // Os PITs já gravados continuam aparecendo no Book: leitura pura do histórico, nunca cria.)

  private async pitSection(context: RequestContext, bookContext: ResolvedBookContext, inspectionIds: string[], applicable: boolean): Promise<BookSection> {
    const authorized = this.can(context, PermissionCode.RiRead) || this.can(context, PermissionCode.IntegrityReportView);
    const rows: BookDocumentRow[] = [];
    if (applicable && authorized) {
      for (const inspectionId of inspectionIds) {
        const book = await this.deps.inspectionBookService.findBook(context, inspectionId as any).catch(() => null);
        if (!book) continue;
        for (const item of book.items) {
          if (item.documentType !== "PIT" || !item.fileUrl) continue;
          rows.push({
            id: `PIT:${item.id}`, origin: "PRONTUARIO_NR13", originLabel: "PIT (Prontuário NR-13)",
            number: item.fileName || "PIT", revision: item.revision || null, date: isoOnly((item as any).updatedAt),
            status: item.status, statusLabel: BOOK_ITEM_STATUS_LABELS[item.status] || item.status, responsible: item.responsibleProfessionalId || null,
            viewRoute: item.fileUrl, downloadRoute: item.fileUrl, isDraft: item.status === "PENDING" || item.status === "NOT_EVALUATED"
          });
        }
      }
      rows.push(...(await this.technicalDocumentRows(context, bookContext, "PIT")));
    }
    rows.sort((a, b) => b.date.localeCompare(a.date));
    return this.buildSection("PITS", applicable, rows, authorized);
  }

  private buildSection(id: BookSectionId, applicable: boolean, rows: BookDocumentRow[], authorized: boolean): BookSection {
    return { id, label: BOOK_SECTION_LABELS[id], applicable, authorized, rows: applicable ? rows : [] };
  }

  /**
   * Monta o `DocumentSnapshot` do Book consolidado do Equipamento — a mesma
   * montagem usada tanto para gerar/baixar o PDF quanto para o VISUALIZAR
   * RELATÓRIO do Studio (item "e" do comando de Identidade Visual do Book):
   * preview e PDF final saem exatamente do mesmo `DocumentSnapshot`/builder,
   * nunca de uma renderização paralela.
   */
  private async buildConsolidatedSnapshot(
    context: RequestContext,
    equipmentId: string,
    pdfContext: { clientLabel?: string; projectLabel?: string; unitAreaLabel?: string } = {}
  ): Promise<{ snapshot: DocumentSnapshot; formalBook: any; equipment: any }> {
    const bookConsolidated = await this.loadBook(context, equipmentId);
    const equipment = await this.deps.equipmentService.getEquipment(context, equipmentId);
    if (!equipment) throw new AppError(ErrorCode.NotFound, "Equipamento não encontrado.");

    const inspections = await this.inspectionsOf(context, equipmentId);
    const primaryInspection = inspections[0] || null;
    const report = primaryInspection && this.deps.reportService?.reportRepo
      ? await this.deps.reportService.reportRepo.findByInspection(context, primaryInspection.id as any).catch(() => null)
      : null;

    let formalBook: any = null;
    let issues: any[] = [];
    if (primaryInspection) {
      formalBook = await this.deps.inspectionBookService.findBook(context, primaryInspection.id as any).catch(() => null);
      if (formalBook) {
        issues = (await this.deps.inspectionBookService.listIssues(context, primaryInspection.id as any).catch(() => [])) as any[];
      }
    }

    if (!formalBook) {
      formalBook = {
        bookNumber: `BOOK-${equipment.tag || equipment.id}`,
        revision: "00",
        status: "DRAFT",
        equipmentId: equipment.id,
        inspectionId: primaryInspection?.id || null,
        items: [],
      };
    }

    // STUDIO INSPECT IA — visual da própria identidade estrutural do Book
    // (capa, seções, matriz); os documentos consolidados dentro dele mantêm
    // a identidade que já traziam. `id` existe só no Book formal (o
    // provisório nunca é carimbado — não há o que carimbar).
    const visual = formalBook.id ? await this.resolveVisual(String(formalBook.id), formalBook.status === "ISSUED") : null;

    const snapshot: DocumentSnapshot = {
      id: formalBook.bookNumber,
      type: "INSPECTION_BOOK",
      revision: formalBook.revision,
      clientId: String(equipment.clientId),
      data: {
        book: formalBook,
        equipment,
        inspection: primaryInspection,
        report,
        issues,
        generatedLineList: bookConsolidated.generatedLineList,
        sections: bookConsolidated.sections,
        pdfContext,
        visual,
      },
      createdAt: new Date(),
    };

    return { snapshot, formalBook, equipment };
  }

  /**
   * GERAÇÃO E DOWNLOAD DO PDF CONSOLIDADO DO BOOK DE INSPEÇÃO.
   * Suporta Book Preliminar/Incompleto (com marca d'água e apêndice de pendências)
   * sem violar o gate oficial de emissão regulatória (InspectionBookService.issueBook).
   */
  async generateConsolidatedPdf(
    context: RequestContext,
    equipmentId: string,
    pdfContext: { clientLabel?: string; projectLabel?: string; unitAreaLabel?: string } = {}
  ): Promise<string> {
    const { snapshot } = await this.buildConsolidatedSnapshot(context, equipmentId, pdfContext);
    return documentEngine.generatePdf(snapshot);
  }

  async downloadConsolidatedPdf(
    context: RequestContext,
    equipmentId: string,
    pdfContext: { clientLabel?: string; projectLabel?: string; unitAreaLabel?: string } = {}
  ): Promise<Uint8Array> {
    const { snapshot, formalBook, equipment } = await this.buildConsolidatedSnapshot(context, equipmentId, pdfContext);
    const safeTag = String(equipment.tag || equipment.name || equipment.id).replace(/[^A-Za-z0-9._-]+/g, "_");
    const targetName = `${formalBook.bookNumber || `BOOK_${safeTag}`}_Rev${formalBook.revision || "00"}.pdf`;
    return documentEngine.downloadPdf(snapshot, targetName);
  }

  /**
   * Snapshot para o VISUALIZAR RELATÓRIO do Studio (12 — Preview): mesma
   * montagem do PDF oficial, sem gerar/baixar nada — quem troca o `visual`
   * por uma cópia em memória é a própria tela (não altera o Equipamento).
   */
  async buildDocumentSnapshot(context: RequestContext, equipmentId: string): Promise<DocumentSnapshot> {
    const { snapshot } = await this.buildConsolidatedSnapshot(context, equipmentId);
    return snapshot;
  }
}
